'use client'

import { useEffect, useState } from 'react'
import { AnimatedNumber } from './animated-number'
import {
  AnimatedStatus,
  AppChrome,
  DeviceFrame,
  StatusBadge,
  type Tone,
} from './visuals'

/**
 * The screen beside the hero: three portal screens, one at a time, turning on a timer.
 *
 * A referral being routed, the KPIs for the month, and an invoice run: the three things
 * the page goes on to describe. The frame's address bar names each one. The turn stops
 * for anyone who asked for reduced motion (the referral stays) and pauses while the tab
 * is hidden. The server renders the first screen, so with no JavaScript the frame shows a
 * finished screen rather than a blank. Every figure is invented.
 *
 * Every screen has one shape at every width (single-line title, three fixed stat columns,
 * four single-line rows, a two-line footer), so the three are the same height and the turn
 * moves nothing around it.
 */
const HOLD_MS = 6000

interface Row {
  readonly text: string
  readonly label: string
  readonly tone: Tone
}

interface Screen {
  readonly address: string
  readonly crumb: string
  readonly title: string
  readonly subtitle: string
  readonly status: string
  readonly statusTone: Tone
  readonly stats: readonly [Stat, Stat, Stat]
  readonly rows: readonly Row[]
  readonly footer: string
}

interface Stat {
  readonly value: number
  readonly label: string
  readonly format?: 'plain' | 'currency' | 'percent'
}

const SCREENS: readonly Screen[] = [
  {
    address: 'wysehub.co.uk/referrals',
    crumb: 'Referrals / REF-48221',
    title: 'Referral REF-48221',
    subtitle: 'Non-fault, hire and repair',
    status: 'Allocating',
    statusTone: 'running',
    stats: [
      { value: 3, label: 'Partners in scope' },
      { value: 72, label: 'Liability score', format: 'percent' },
      { value: 14, label: 'Minutes open' },
    ],
    rows: [
      { text: 'Claim received from feed', label: 'Done', tone: 'success' },
      { text: 'Liability scored', label: 'Done', tone: 'success' },
      {
        text: 'Allocated on hire type, location and conversion',
        label: 'Allocated',
        tone: 'success',
      },
      { text: 'Partner notified', label: 'Sending', tone: 'running' },
    ],
    footer:
      'Allocated by rule to the hire and repair partner converting best in the claimant’s area.',
  },
  {
    address: 'wysehub.co.uk/kpis',
    crumb: 'KPI dashboard / This month',
    title: 'This month',
    subtitle: 'All partners',
    status: 'On track',
    statusTone: 'success',
    stats: [
      { value: 412, label: 'Allocations' },
      { value: 96, label: 'On time', format: 'percent' },
      { value: 11, label: 'Minutes to allocate' },
    ],
    rows: [
      { text: 'Hire and repair', label: '188', tone: 'unknown' },
      { text: 'Repair only', label: '131', tone: 'unknown' },
      { text: 'Total loss', label: '64', tone: 'unknown' },
      { text: 'Awaiting partner acceptance', label: '3', tone: 'running' },
    ],
    footer: 'Exported to your reporting on the first of the month.',
  },
  {
    address: 'wysehub.co.uk/invoices',
    crumb: 'Invoices / August',
    title: 'Invoice run',
    subtitle: 'August, all partners',
    status: 'Ready',
    statusTone: 'success',
    stats: [
      { value: 412, label: 'Allocations' },
      { value: 6, label: 'Partners' },
      { value: 38460, label: 'Total', format: 'currency' },
    ],
    rows: [
      {
        text: 'Rates applied from partner agreements',
        label: 'Done',
        tone: 'success',
      },
      {
        text: 'Lines checked against allocations',
        label: 'Done',
        tone: 'success',
      },
      { text: 'Invoices raised', label: 'Raised', tone: 'success' },
      { text: 'Sent to accounts', label: 'Queued', tone: 'running' },
    ],
    footer: 'Every line traces back to the claim it came from.',
  },
]

export function HeroFrames() {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const timer = window.setInterval(() => {
      if (document.hidden) return
      setIndex((current) => (current + 1) % SCREENS.length)
    }, HOLD_MS)
    return () => window.clearInterval(timer)
  }, [])

  const screen = SCREENS[index] ?? SCREENS[0]
  if (!screen) return null

  return (
    <DeviceFrame className="tilt" label={screen.address} padded={false}>
      <ScreenPanel key={screen.address} screen={screen} />
    </DeviceFrame>
  )
}

function ScreenPanel({ screen }: { readonly screen: Screen }) {
  const animated = screen.rows.findIndex((row) => row.tone === 'running')

  return (
    <div className="loops enter">
      <AppChrome crumb={screen.crumb}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-display text-xl font-bold tracking-tight text-ink">
              {screen.title}
            </p>
            <p className="mt-1 truncate text-xs text-ink-muted">
              {screen.subtitle}
            </p>
          </div>
          <StatusBadge tone={screen.statusTone} label={screen.status} />
        </div>

        <div className="mt-5 grid grid-cols-3 gap-x-3">
          {screen.stats.map((stat) => (
            <div className="min-w-0" key={stat.label}>
              <p className="font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">
                <AnimatedNumber value={stat.value} format={stat.format} />
              </p>
              <p className="mt-1 whitespace-nowrap text-xs text-ink-muted">
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        <ul className="mt-6 flex flex-col border-t border-line">
          {screen.rows.map((row, rowIndex) => (
            <li
              className="flex items-center justify-between gap-3 border-b border-line py-2.5 last:border-0"
              key={row.text}
            >
              <span className="min-w-0 truncate text-sm text-ink">
                {row.text}
              </span>
              {rowIndex === animated ? (
                <AnimatedStatus
                  from="running"
                  to="success"
                  fromLabel={row.label}
                  toLabel="Done"
                  loopIndex={1}
                />
              ) : (
                <StatusBadge tone={row.tone} label={row.label} />
              )}
            </li>
          ))}
        </ul>

        <p className="mt-4 min-h-[2lh] text-xs text-ink-muted">
          {screen.footer}
        </p>
      </AppChrome>
    </div>
  )
}
